import React, { useMemo, useState, useEffect } from "react";
import { queueMemo, scheduleDailyPosts, getQueue, getDailyPostCount, getNextPostTime, forcePostNow, addTestItem, clearQueue } from "./xPosting";
import { useWallet } from "@solana/wallet-adapter-react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { Transaction, SystemProgram, TransactionInstruction, PublicKey, LAMPORTS_PER_SOL } from "@solana/web3.js";
import sha256 from "js-sha256";
import { SacrificeInterface } from "./components/SacrificeInterface";

// BAD SEED wallet address (receiver)
const BAD_SEED_WALLET_ADDRESS = "9TyzcephhXEw67piYNc72EJtgVmbq3AZhyPFSvdfXWdr";

// Solana RPC endpoint (mainnet)
// 2) SOLANA_RPC_ENDPOINT is defined later in the file (using Helius)

// Memo program ID
const MEMO_PROGRAM_ID = new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr");

// Deliberately small, local icon system.  BADSEED does not use emoji as UI.
function Glyph({ name, label }) {
  const paths = {
    copy: <><rect x="8" y="8" width="10" height="10" rx="1" /><path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" /></>,
    archive: <><path d="M4 7h16v13H4z" /><path d="M3 3h18v4H3zM9 11h6" /></>,
    signal: <><path d="M4 18a8 8 0 0 1 16 0" /><path d="M7 18a5 5 0 0 1 10 0M10 18a2 2 0 0 1 4 0" /><circle cx="12" cy="18" r="1" /></>,
    pulse: <><path d="M3 12h4l2-5 4 10 2-5h6" /></>,
    eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></>,
    warning: <><path d="M12 3 2.8 20h18.4L12 3Z" /><path d="M12 9v5M12 17h.01" /></>,
    external: <><path d="M14 4h6v6" /><path d="m20 4-9 9" /><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" /></>,
    chevron: <path d="m9 18 6-6-6-6" />,
    status: <><circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2" /></>,
  };
  return <svg className="glyph" viewBox="0 0 24 24" role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{paths[name] || paths.status}</svg>;
}

// ---------- localStorage cache helpers ----------
function getCachedLog(signature) {
  try {
    const cached = localStorage.getItem(`badseed_ai_log_${signature}`);
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    return null;
  }
}

function saveCachedLog(signature, log) {
  try {
    localStorage.setItem(`badseed_ai_log_${signature}`, JSON.stringify(log));
  } catch (e) {
    console.warn("Failed to cache AI log:", e);
  }
}

// ---------- Blacklist helpers ----------
const DEFAULT_BLACKLIST = [
  "EZvp2MfKaqZ14D95EMSECXfGqduScMCSUzpKSxBcNTzM",
  "AoX3EMzVXCNBdCNvboc7yGM4gsr3wcKd7hGsZ4yXcydU",
  "FLipG5QHjZe1H12f6rr5LCnrmqjhwuBTBp78GwzxnwkR"
];

// Detect if running locally (localhost) vs deployed
function isLocalEnvironment() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function getBlacklistedAddresses() {
  // Always return DEFAULT_BLACKLIST from code
  return DEFAULT_BLACKLIST;
}


function copyBlacklistToClipboard(blacklist) {
  const arrayString = `const DEFAULT_BLACKLIST = [\n${blacklist.map(addr => `  "${addr}"`).join(',\n')}\n];`;
  navigator.clipboard.writeText(arrayString).then(() => {
    alert('Blacklist copied to clipboard! Paste into App.js DEFAULT_BLACKLIST and commit.');
  }).catch(err => {
    console.error('Failed to copy:', err);
    prompt('Copy this array definition:', arrayString);
  });
}
// // duplicate import removed // duplicate removed
// BADSEED AI: v1.0 - Real OpenAI Integration Active

// Generator UI disabled - removed to avoid polyfill issues

// ===========================
// CONFIG: SEED + SOLANA
// ===========================

// 1) BAD_SEED_WALLET_ADDRESS is defined at the top of the file


// 2) Seed phrase (base64-encoded to avoid plain text in source)
//
// TODO after you choose a seed you like, take the base64 string printed
//      by the generator and paste it here.
const ENCODED_SEED_PHRASE =
  "YmFkIHNlZWQgZXhwZXJpbWVudCBwdWJsaWMgc2hhcmVkIHdhbGxldCBvcGVuIGNvbGxlY3RpdmUgc2lnbmFsIGNoYW9zIGJhbGFuY2UgZmx1eA==";

// Obfuscated RPC configuration (reconstructed at runtime)
const RPC_BASE = "aHR0cHM6Ly9tYWlubmV0LmhlbGl1cy1ycGMuY29tLw==";
const RPC_PARAM = "P2FwaS1rZXk9";
const RPC_KEY_P1 = "NjVjZmE5Zjc=";
const RPC_KEY_P2 = "N2JmZS00NGZm";
const RPC_KEY_P3 = "OGU5OC0yNGZmODBiMDFlOGM=";
const SOLANA_RPC_ENDPOINT = atob(RPC_BASE) + atob(RPC_PARAM) + atob(RPC_KEY_P1) + "-" + atob(RPC_KEY_P2) + "-" + atob(RPC_KEY_P3);

// ===========================
// Helper: decode base64 seed
// ===========================
function decodeSeedPhrase(encoded) {
  try {
    if (!encoded) return "";
    return window.atob(encoded);
  } catch (e) {
    console.warn("Failed to decode seed phrase:", e);
    return "";
  }
}

// ===========================
// Helper: Extract bracket number from memo
// Memos contain character count in format [N] e.g. "Hello world [11]"
// ===========================
function extractMemoCharCount(memo) {
  if (!memo) return 0;
  // Match [number] pattern - typically at the end of memo
  const match = memo.match(/\[(\d+)\]/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return 0;
}

// ===========================
// Helper: Get UTC day boundaries
// ===========================
function getUtcDayBoundaries() {
  const now = new Date();
  // Start of current UTC day (00:00:00 UTC)
  const startOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
  // End of current UTC day (23:59:59.999 UTC)
  const endOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
  return { startOfDay, endOfDay };
}

// ===========================
// Helper: Calculate daily memo count from transactions
// ===========================
function calculateDailyMemoCount(transactions) {
  const { startOfDay, endOfDay } = getUtcDayBoundaries();
  let total = 0;

  transactions.forEach(tx => {
    if (!tx.blockTime || !tx.memo) return;

    // Convert blockTime (Unix timestamp in seconds) to Date
    const txTime = new Date(tx.blockTime * 1000);

    // Check if transaction is within current UTC day
    if (txTime >= startOfDay && txTime <= endOfDay) {
      total += extractMemoCharCount(tx.memo);
    }
  });

  return total;
}

// ===========================
// Helper: Solana JSON-RPC
// ===========================
async function solanaRpc(method, params) {
  const body = {
    jsonrpc: "2.0",
    id: 1,
    method,
    params,
  };

  const res = await fetch(SOLANA_RPC_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`Solana RPC HTTP error ${res.status}`);
  }

  const json = await res.json();
  if (json.error) {
    throw new Error(`Solana RPC error: ${JSON.stringify(json.error)}`);
  }

  return json.result;
}

// Fetch AI narrative logs from Netlify serverless function (calls OpenAI)
async function fetchAiLogsForTransactions(transactions, balanceSol, walletAddress, totalTxCount, recentHistory) {
  if (!transactions || transactions.length === 0) {
    return [];
  }

  try {
    const response = await fetch("/.netlify/functions/ai-narrative", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        balanceSol,
        transactions: transactions.map((tx) => ({
          signature: tx.signature || "",
          slot: tx.slot ?? null,
          blockTime: tx.blockTime ?? null,
          confirmationStatus: tx.confirmationStatus || "unknown",
          err: tx.err || null,
          memo: tx.memo || null,
          type: tx.type || "Unknown",
          direction: tx.direction || "Unknown",
          amount: tx.amount || "0",
          token: tx.token || "SOL",
          // Pass context as part of first transaction for simplicity
          ...(transactions.indexOf(tx) === 0 ? {
            walletAddress,
            totalTxCount,
            recentHistory
          } : {})
        }))
      })
    });

    if (!response.ok) {
      console.error("AI narrative endpoint error:", response.status);
      return transactions.map((tx, idx) =>
        `[SEED_LOG] slot=${tx.slot ?? "?"} idx=${idx} note="AI endpoint unavailable."`
      );
    }

    const data = await response.json();

    if (!data || !Array.isArray(data.logs)) {
      console.warn("AI narrative returned unexpected format:", data);
      return transactions.map((tx, idx) =>
        `[SEED_LOG] slot=${tx.slot ?? "?"} idx=${idx} note="AI format error."`
      );
    }

    // Process sentiments if provided
    if (data.sentiments && Array.isArray(data.sentiments)) {
      // Update sentiment for each non-empty sentiment
      for (const sentiment of data.sentiments) {
        if (sentiment && ['hope', 'greed', 'fear', 'mystery'].includes(sentiment)) {
          try {
            const sentRes = await fetch('/.netlify/functions/sentiment-update', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sentiment })
            });
            if (!sentRes.ok) {
              const errText = await sentRes.text();
              console.error('Sentiment update failed:', sentRes.status, errText);
            } else {
              const sentData = await sentRes.json();
              if (sentData.binId) {
                console.warn("Save this to Netlify environment variables.");
                console.log("SENTIMENT_BIN_ID=" + sentData.binId);
              }
            }
          } catch (err) {
            console.error('Failed to update sentiment:', err);
          }
        }
      }
    }

    // Normalize logs to match transaction count
    return transactions.map((_, idx) => {
      return typeof data.logs[idx] === "string"
        ? data.logs[idx]
        : `[SEED_LOG] slot=${transactions[idx].slot ?? "?"} note="AI log missing."`;
    });
  } catch (err) {
    console.error("Error fetching AI logs:", err);
    return transactions.map((tx, idx) =>
      `[SEED_LOG] slot=${tx.slot ?? "?"} idx=${idx} note="AI connection failed."`
    );
  }
}

function App() {
  const [showDashboard, setShowDashboard] = useState(false);
  const [viewingData, setViewingData] = useState(null); // State for Data Viewer Modal (Fixed RefError via HMR)
  const [balanceText, setBalanceText] = useState("Loading…");
  const [txItems, setTxItems] = useState([]);
  const [aiLogs, setAiLogs] = useState([]); // AI terminal logs per transaction
  const [postQueue, setPostQueue] = useState([]); // X.com post queue
  const [dailyPostCount, setDailyPostCount] = useState(0);
  const [nextPostTime, setNextPostTime] = useState(null);
  const [blacklist, setBlacklist] = useState([]);
  const [blacklistedTxs, setBlacklistedTxs] = useState([]);
  const [newBlacklistAddress, setNewBlacklistAddress] = useState("");

  // Wallet connection
  const { publicKey, sendTransaction } = useWallet();

  // Send message modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [memoText, setMemoText] = useState("");
  const [solAmount, setSolAmount] = useState("0.001");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState(false);

  // Logo pulse enhancement
  const [logoPulseEnhanced, setLogoPulseEnhanced] = useState(false);
  const [showSacrificeModal, setShowSacrificeModal] = useState(false);
  const [isSacrificeVisible, setIsSacrificeVisible] = useState(true); // Default to true, update from config

  // Sentiment & Prophecy state
  const [sentimentData, setSentimentData] = useState(null);
  const [prophecy, setProphecy] = useState(null);

  // Daily memo character count (sum of [N] bracket numbers for current UTC day)
  const [dailyMemoCount, setDailyMemoCount] = useState(0);

  function triggerLogoPulse() {
    setLogoPulseEnhanced(true);
    setTimeout(() => setLogoPulseEnhanced(false), 5000);
  }

  // Fetch DApp Config for Visibility
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const confRes = await fetch('/.netlify/functions/dapp-config');
        if (confRes.ok) {
          const confData = await confRes.json();
          // If key is missing, default to true
          setIsSacrificeVisible(confData.isSacrificeVisible !== false);
        }
      } catch (e) {
        console.warn("Config fetch failed, defaulting visibility to true");
      }
    };
    fetchConfig();
  }, []);

  // Track wallet connections for analytics
  useEffect(() => {
    if (publicKey) {
      const trackWallet = async () => {
        try {
          await fetch('/.netlify/functions/analytics-track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              walletAddress: publicKey.toString(),
              eventType: 'wallet_connect'
            })
          });
          console.log('Wallet connection tracked');
        } catch (error) {
          console.warn('Analytics tracking failed:', error);
        }
      };
      trackWallet();
    }
  }, [publicKey]);

  // Effect to trigger pulse when modal opens
  useEffect(() => {
    if (showSendModal) {
      triggerLogoPulse();
    }
  }, [showSendModal]);

  // Auto-refresh X.com queue display every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      updateQueueDisplay();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch sentiment data
  const fetchSentiment = async () => {
    try {
      const response = await fetch('/.netlify/functions/sentiment-get');
      const data = await response.json();
      setSentimentData(data);
    } catch (error) {
      console.error('Failed to fetch sentiment:', error);
    }
  };

  // Fetch prophecy data
  const fetchProphecy = async () => {
    try {
      const response = await fetch('/.netlify/functions/prophecy-get');
      const data = await response.json();
      setProphecy(data);
    } catch (error) {
      console.error('Failed to fetch prophecy:', error);
    }
  };

  // Initial fetch and polling for Sentiment
  useEffect(() => {
    fetchSentiment(); // Initial fetch

    const interval = setInterval(() => {
      if (!document.hidden) { // Only fetch if page is visible
        fetchSentiment();
      }
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  // Initial fetch and polling for Prophecy
  useEffect(() => {
    fetchProphecy(); // Initial fetch

    const interval = setInterval(fetchProphecy, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  // DEV generator state
  // Dev generator state removed

  // Decode seed phrase once for the main BAD SEED wallet
  const seedPhrase = useMemo(
    () => decodeSeedPhrase(ENCODED_SEED_PHRASE),
    []
  );

  const seedWords = useMemo(
    () =>
      seedPhrase
        ? seedPhrase.split(" ").filter((w) => w.trim().length > 0)
        : [],
    [seedPhrase]
  );

  // Load Solana data when dashboard is shown (no auto-refresh)
  useEffect(() => {
    if (showDashboard) {
      loadWalletData();
      // start the twice‑daily X.com posting scheduler
      scheduleDailyPosts();
      // Update queue display
      updateQueueDisplay();
      // Load blacklist from code (DEFAULT_BLACKLIST)
      setBlacklist(getBlacklistedAddresses());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDashboard]);


  // Blacklist management functions (in-memory for local dev)
  function handleAddToBlacklist() {
    const trimmed = newBlacklistAddress.trim();
    if (trimmed && trimmed.length > 30) { // Basic Solana address validation
      if (!blacklist.includes(trimmed)) {
        setBlacklist([...blacklist, trimmed]);
        setNewBlacklistAddress("");
      }
    }
  }

  function handleRemoveFromBlacklist(address) {
    setBlacklist(blacklist.filter(addr => addr !== address));
  }

  function handleCopyBlacklist() {
    copyBlacklistToClipboard(blacklist);
  }

  async function handleCopyAndEnter() {
    const phrase = seedPhrase || "";

    if (!phrase) {
      alert("Seed phrase is not configured yet.");
      return;
    }

    try {
      await navigator.clipboard.writeText(phrase);
    } catch (err) {
      console.warn("Clipboard copy failed, falling back to prompt:", err);
      window.prompt("Copy this seed phrase manually:", phrase);
    }

    // Trigger the transition to the dashboard
    setShowDashboard(true);
  }

  // Send transaction with SOL + memo
  async function handleSendTransaction() {
    setSendError("");
    setSendSuccess(false);

    // Validation
    if (!memoText.trim()) {
      setSendError("Please enter a message");
      return;
    }
    // Enforce max 100 characters for memo
    if (memoText.length > 100) {
      setSendError("Memo must be 100 characters or less");
      return;
    }

    const amount = parseFloat(solAmount);
    if (isNaN(amount) || amount < 0.001) {
      setSendError("Amount must be at least 0.001 SOL");
      return;
    }

    if (!publicKey) {
      setSendError("Please connect your wallet first");
      return;
    }

    setIsSending(true);

    try {
      // Create connection (use same RPC endpoint as dashboard)
      const { Connection } = await import("@solana/web3.js");
      const connection = new Connection(SOLANA_RPC_ENDPOINT);

      // Create transaction
      const transaction = new Transaction();

      // 1. Add SOL transfer instruction
      const lamports = Math.floor(amount * LAMPORTS_PER_SOL);
      const transferIx = SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: new PublicKey(BAD_SEED_WALLET_ADDRESS),
        lamports,
      });
      transaction.add(transferIx);

      // 2. Add memo instruction
      const memoData = Buffer.from(memoText.trim(), "utf-8");
      const memoIx = new TransactionInstruction({
        keys: [],
        programId: MEMO_PROGRAM_ID,
        data: memoData,
      });
      transaction.add(memoIx);

      // Get recent blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Send transaction (wallet will sign)
      const signature = await sendTransaction(transaction, connection);

      console.log("Transaction sent:", signature);

      // Success
      setSendSuccess(true);

      // Reload wallet data after a short delay to fetch the new transaction
      // This will trigger AI analysis and queue the memo for X.com
      setTimeout(async () => {
        setShowSendModal(false);
        setMemoText("");
        setSolAmount("0.001");
        setSendSuccess(false);

        // Wait a bit more for blockchain confirmation, then refresh
        setTimeout(() => {
          loadWalletData();
        }, 3000); // Wait 3 seconds for transaction to confirm
      }, 2000);

    } catch (err) {
      console.error("Transaction error:", err);
      if (err.message && err.message.includes("User rejected")) {
        setSendError("Transaction cancelled");
      } else {
        setSendError(err.message || "Transaction failed. Please try again.");
      }
    } finally {
      setIsSending(false);
    }
  }


  // After AI logs are fetched and cached, forward any new memos
  // This is called inside loadWalletData after setAiLogs(logs);
  // This is called inside loadWalletData after setAiLogs(logs);
  async function forwardMemosIfNeeded(processedTxs, logsArray) {
    const currentQueue = await getQueue();
    const queueArray = Array.isArray(currentQueue) ? currentQueue : [];
    const queuedSignatures = new Set(queueArray.map(item => item.hash || sha256(item.memo)));

    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();

    for (const tx of processedTxs) {
      if (tx.memo) {
        // 1. Check age: Skip if transaction is older than 24 hours
        // tx.blockTime is in seconds. If missing, we can't judge age (safe to skip or process? process risks loop. skip risks miss.)
        // Let's assume valid blockTime for confirmed txs.
        if (tx.blockTime) {
          const txTimeMs = tx.blockTime * 1000;
          if (now - txTimeMs > TWENTY_FOUR_HOURS_MS) {
            // Too old, ignore
            continue;
          }
        }

        // Check if this memo is already queued (using hash of memo)
        const memoHash = sha256(tx.memo);
        if (!queuedSignatures.has(memoHash)) {
          const aiLog = logsArray[processedTxs.indexOf(tx)];
          if (aiLog) {
            // Queue memo + AI log for later X.com posting
            // Convert blockTime (seconds) to ISO string, or fallback to now
            const txTime = tx.blockTime ? new Date(tx.blockTime * 1000).toISOString() : new Date().toISOString();
            queueMemo(tx.memo, aiLog, txTime);
            console.log("Queued memo:", tx.memo, "with AI log:", aiLog, "Timestamp:", txTime);
          }
        }
      }
    }
  }

  async function updateQueueDisplay() {
    const queue = await getQueue();
    setPostQueue(queue);
    setDailyPostCount(getDailyPostCount());
    setNextPostTime(getNextPostTime());
  }

  async function loadWalletData() {
    if (!BAD_SEED_WALLET_ADDRESS) {
      setBalanceText("Set BAD_SEED_WALLET_ADDRESS in App.js");
      setTxItems([
        {
          signature: "CONFIG_PLACEHOLDER",
          confirmationStatus: "unknown",
          slot: "?",
          blockTime: null,
          note: "Configure the wallet address to load real data.",
        },
      ]);
      return;
    }

    setBalanceText("Loading…");

    try {
      // 1. Get Balance
      const balanceResult = await solanaRpc("getBalance", [
        BAD_SEED_WALLET_ADDRESS,
        { commitment: "finalized" },
      ]);
      const lamports = balanceResult.value ?? 0;
      const sol = lamports / 1_000_000_000;
      setBalanceText(`${sol.toFixed(9)} SOL`);

      // 2. Get Recent Signatures
      const sigResult = await solanaRpc("getSignaturesForAddress", [
        BAD_SEED_WALLET_ADDRESS,
        { limit: 10 }, // Increased limit to see more activity
      ]);

      if (!Array.isArray(sigResult) || sigResult.length === 0) {
        setTxItems([]);
        setAiLogs([]);
        return;
      }

      // 3. Get Parsed Transaction Details (for USDC/SPL support)
      // Note: Helius free tier might not support batch getParsedTransactions, so we use parallel getTransaction
      const signatures = sigResult.map(s => s.signature);

      const txDetails = await Promise.all(
        signatures.map(async (sig) => {
          try {
            return await solanaRpc("getTransaction", [
              sig,
              { maxSupportedTransactionVersion: 0, commitment: "finalized", encoding: "jsonParsed" }
            ]);
          } catch (e) {
            console.warn(`Failed to fetch details for ${sig}`, e);
            return null;
          }
        })
      );

      // Process transactions to extract useful info (amount, direction, token)
      const processedTxs = sigResult.map((sigEntry, idx) => {
        const detail = txDetails && txDetails[idx] ? txDetails[idx] : null;
        let type = "Unknown";
        let amount = "";
        let direction = "";
        let token = "SOL";

        if (detail) {
          const meta = detail.meta;
          const transaction = detail.transaction;

          // Check for SOL balance change
          const accountIndex = transaction.message.accountKeys.findIndex(
            k => (k.pubkey || k) === BAD_SEED_WALLET_ADDRESS
          );

          if (accountIndex !== -1 && meta) {
            const pre = meta.preBalances[accountIndex];
            const post = meta.postBalances[accountIndex];
            const diff = post - pre;

            if (diff !== 0) {
              const solDiff = diff / 1_000_000_000;
              direction = solDiff > 0 ? "IN" : "OUT";
              amount = Math.abs(solDiff).toFixed(4);
              type = "Transfer";
            }
          }

          // Check for Token balance changes (USDC, etc)
          if (meta && meta.preTokenBalances && meta.postTokenBalances) {
            // Simple logic: find any token balance change for this wallet
            // This is a simplification; robust parsing is complex
            const preToken = meta.preTokenBalances.find(b => b.owner === BAD_SEED_WALLET_ADDRESS);
            const postToken = meta.postTokenBalances.find(b => b.owner === BAD_SEED_WALLET_ADDRESS);

            if (preToken || postToken) {
              const preAmt = preToken ? preToken.uiTokenAmount.uiAmount : 0;
              const postAmt = postToken ? postToken.uiTokenAmount.uiAmount : 0;
              const tokenDiff = postAmt - preAmt;

              if (tokenDiff !== 0) {
                direction = tokenDiff > 0 ? "IN" : "OUT";
                amount = Math.abs(tokenDiff).toFixed(2);
                token = "SPL"; // Could look up mint, but keeping it simple for now
                type = "Token Transfer";
                // Try to guess USDC based on decimals/mint if needed, but generic SPL is safer
                if (postToken && postToken.mint === "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v") {
                  token = "USDC";
                }
              }
            }
          }
        }

        return {
          ...sigEntry,
          type,
          amount,
          direction,
          token,
          note: "On-chain activity"
        };
      });

      // Filter transactions by blacklist
      const currentBlacklist = getBlacklistedAddresses();
      const displayed = [];
      const blacklisted = [];

      processedTxs.forEach(tx => {
        // Check if transaction involves any blacklisted address
        // We need to check the transaction details for account keys
        const txIndex = processedTxs.indexOf(tx);
        const detail = txDetails && txDetails[txIndex] ? txDetails[txIndex] : null;

        let isBlacklisted = false;
        if (detail && detail.transaction && detail.transaction.message) {
          const accountKeys = detail.transaction.message.accountKeys || [];
          // Check if any account matches blacklist
          isBlacklisted = accountKeys.some(key => {
            const address = typeof key === 'string' ? key : key.pubkey || key;
            return currentBlacklist.includes(address);
          });
        }

        if (isBlacklisted) {
          blacklisted.push(tx);
        } else {
          displayed.push(tx);
        }
      });

      setTxItems(displayed);
      setBlacklistedTxs(blacklisted);

      // Calculate daily memo character count (sum of [N] bracket numbers for current UTC day)
      const dailyCount = calculateDailyMemoCount(displayed);
      setDailyMemoCount(dailyCount);

      // 4. AI Logs with Caching (only for displayed transactions)
      // Check cache first
      const logs = [];
      const txsToFetch = [];
      const txsToFetchIndices = [];

      displayed.forEach((tx, i) => {
        const cached = getCachedLog(tx.signature);
        if (cached) {
          logs[i] = cached;
        } else {
          logs[i] = null; // Placeholder
          txsToFetch.push(tx);
          txsToFetchIndices.push(i);
        }
      });

      if (txsToFetch.length > 0) {
        // Build recent history context (last 5-10 transactions with memos and AI logs)
        const recentHistory = displayed.slice(0, 10).map((tx) => ({
          signature: tx.signature,
          type: tx.type || "Unknown",
          direction: tx.direction || "",
          amount: tx.amount || "",
          token: tx.token || "SOL",
          memo: tx.memo || null,
          aiLog: getCachedLog(tx.signature) || null
        }));

        // Fetch only missing logs with full context
        const fetchedLogs = await fetchAiLogsForTransactions(
          txsToFetch,
          sol,
          BAD_SEED_WALLET_ADDRESS,
          displayed.length,
          recentHistory
        );

        // Merge and save to cache
        fetchedLogs.forEach((log, fetchIdx) => {
          const originalIdx = txsToFetchIndices[fetchIdx];
          logs[originalIdx] = log;
          saveCachedLog(displayed[originalIdx].signature, log);
        });
      }

      setAiLogs(logs);
      // Forward any new memos to X.com after logs are stored
      await forwardMemosIfNeeded(displayed, logs);

      // Refresh Queue & Sentiment (ensure UI is in sync)
      await updateQueueDisplay();
      await fetchSentiment();


    } catch (err) {
      console.error("Error loading Solana data:", err);
      // Only show error in UI if we failed to load critical data (balance/txs)
      // If we have data but failed on AI/Queue steps, keep showing the data
      if (!balanceText || balanceText === "Loading...") {
        setBalanceText("Error loading balance");
      }
      if (txItems.length === 0) {
        setTxItems([
          {
            signature: "RPC_ERROR",
            confirmationStatus: "unknown",
            slot: "?",
            blockTime: null,
            note: "Error loading transactions. RPC might be rate-limited or unavailable.",
          },
        ]);
      }
    }
  }

  // ===========================
  // DEV: generate a new seed on click
  // ===========================
  // Generator function removed

  // Archive Data State
  const [archiveData, setArchiveData] = useState({ pending: [], history: [], chaosMode: false });

  // Fetch Archive Data
  useEffect(() => {
    async function fetchArchive() {
      try {
        const res = await fetch('/.netlify/functions/archive-get');
        if (res.ok) {
          const data = await res.json();
          setArchiveData(data);
        }
      } catch (err) {
        console.error("Archive fetch failed:", err);
      }
    }
    if (showDashboard) {
      fetchArchive();
      // Poll every minute
      const interval = setInterval(fetchArchive, 60000);
      return () => clearInterval(interval);
    }
  }, [showDashboard]);

  return (
    <div id="app">
      {/* Wallet Controls - Fixed top right corner of page */}
      <div className="wallet-controls">
        <button
          className="send-message-btn"
          onClick={() => setShowSendModal(true)}
          disabled={!publicKey || isSending}
          style={{ display: publicKey ? 'block' : 'none' }}
        >
          Send Message to the Seed
        </button>
        <WalletMultiButton />
      </div>

      {/* Sacrifice/Swap Button - Top Left, only visible in Dashboard AND (Local OR Enabled in Config) */}
      {showDashboard && (isLocalEnvironment() || isSacrificeVisible) && (
        <button
          onClick={() => setShowSacrificeModal(true)}
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 9999,
            cursor: 'pointer',
            background: 'rgba(0, 0, 0, 0.6)',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            padding: '10px 20px',
            fontFamily: 'monospace',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = 'white';
            e.currentTarget.style.color = 'white';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)';
          }}
        >
          Sacrifice
        </button>
      )}

      {/* Sacrifice Modal */}
      {showSacrificeModal && (
        <SacrificeInterface onClose={() => setShowSacrificeModal(false)} />
      )}

      {/* Wallet Address Display - Seed Screen Only */}
      {!showDashboard && (
        <div
          className="seed-wallet-address fade-out-target"
          onClick={() => {
            navigator.clipboard.writeText(BAD_SEED_WALLET_ADDRESS);
            triggerLogoPulse();
          }}
          title="Click to copy address"
        >
          <Glyph name="copy" label="Copy wallet address" />
          {BAD_SEED_WALLET_ADDRESS.slice(0, 4)}...{BAD_SEED_WALLET_ADDRESS.slice(-4)}
        </div>
      )}

      {/* LOGO */}
      <header className="site-header">
        <img src="/logo.gif" alt="Bad Seed Logo" className={`logo ${logoPulseEnhanced ? 'logo--pulse-enhanced' : ''}`} />
      </header>

      {/* SEED SCREEN */}
      <main
        id="seed-screen"
        className={
          "screen" + (!showDashboard ? " screen--active screen--fade-in" : "")
        }
      >
        <div className="content-wrapper seed-content">
          {/* Add Archive Status Widget to Seed Screen (optional, or keep in dashboard) */}
          {/* Moving to Dashboard only for cleaner look */}
          {/* Seed Phrase Grid (Restored) */}
          <div className="seed-phrase-container">
            {seedWords.map((word, index) => (
              <div key={index} className="seed-word" data-index={index + 1}>
                {word}
              </div>
            ))}
          </div>

          {/* Explicit Copy & Enter Button */}
          <button className="btn btn-primary" onClick={handleCopyAndEnter} style={{ marginBottom: '2rem', zIndex: 10 }}>
            COPY SEED & ENTER
          </button>

          <div className="seed-container">
            <div
              className={`seed-organism ${logoPulseEnhanced ? 'seed--pulse-enhanced' : ''}`}
              onClick={handleCopyAndEnter}
              title="Click to Enter Dashboard"
            >
              <div className="core"></div>
              <div className="membrane"></div>
            </div>
          </div>
        </div>
      </main>

      {/* DASHBOARD SCREEN */}
      <main
        id="dashboard-screen"
        className={"screen" + (showDashboard ? " screen--active screen--fade-in" : "")}
      >
        <div className="content-wrapper dashboard-content">
          <a className="badseed-x-link" href="https://x.com/BADSEEDEXPOSED" target="_blank" rel="noopener noreferrer" aria-label="BADSEED on X (opens in a new tab)">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.3L12 14.6 5.5 22H2.3l7.9-9L2.5 2H9l4.5 6.7L18.9 2Zm-1.1 18h1.7L8.1 3.9H6.3L17.8 20Z"/></svg>
            <span>@BADSEEDEXPOSED</span><span aria-hidden="true">↗</span>
          </a>
          <div className="seed-monument" aria-hidden="true">
            <div className="monument-word">BAD<span>SEED</span></div>
            <svg className="monument-field" viewBox="0 0 1000 260" fill="none">
              <defs><linearGradient id="silver-field"><stop stopColor="#626976"/><stop offset=".48" stopColor="#f3f5f8"/><stop offset="1" stopColor="#626976"/></linearGradient></defs>
              <g stroke="url(#silver-field)" strokeWidth=".65">
                {Array.from({length:26}, (_, i) => <ellipse key={i} cx="500" cy="130" rx={105+i*12} ry={20+i*3.5} transform={`rotate(${i*6.92} 500 130)`} />)}
              </g>
            </svg>
          </div>
          {/* Header Stats */}
          {/* 1. Wallet Overview (Restored per user request) */}
          <div className="dashboard-card dashboard-card--glow wallet-panel">
            <h2 className="section-title">WALLET OVERVIEW</h2>

            <div className="wallet-row">
              <span className="label">ADDRESS</span>
              <span className="value mono">
                {publicKey ? (
                  <span className="tx-hash">
                    {publicKey.toString().slice(0, 4) + "..." + publicKey.toString().slice(-4)}
                  </span>
                ) : (
                  <span className="tx-hash">
                    {BAD_SEED_WALLET_ADDRESS.slice(0, 4) + "..." + BAD_SEED_WALLET_ADDRESS.slice(-4)}
                  </span>
                )}
              </span>
            </div>

            <div className="wallet-row">
              <span className="label">NETWORK</span>
              <span className="value mono">Solana mainnet-beta</span>
            </div>

            <div className="wallet-row">
              <span className="label">BALANCE</span>
              <span className="value mono glitch-text" data-text={balanceText}>
                {balanceText}
              </span>
            </div>

            <button
              className="btn-refresh hover-effect"
              onClick={loadWalletData}
              style={{
                width: '100%',
                marginTop: '1.5rem',
                background: 'transparent',
                border: '1px solid #666',
                color: '#fff',
                padding: '10px',
                borderRadius: '2px',
                cursor: 'pointer',
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#aaa'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#666'}
            >
              Refresh Data
            </button>
          </div>

          {/* 2. Archive Status (New separate section) */}
          <div className={`dashboard-card archive-panel ${archiveData.chaosMode ? 'chaos-border' : ''}`}>
            <h2 className="section-title">ARCHIVE STATUS</h2>
            <div className="wallet-row" style={{ borderBottom: 'none', paddingBottom: '4px' }}>
              <span className="label">PERMANENCE</span>
              {archiveData.chaosMode ? (
                <span className="value status-chaos">
                  <Glyph name="warning" /> TOPPLED
                  <span className="sub-value" style={{ display: 'block', fontSize: '0.8rem', marginTop: '4px', textAlign: 'right' }}>
                    {archiveData.pending.length} DAY{archiveData.pending.length !== 1 ? 'S' : ''} PENDING
                    {archiveData.pending.length > 0 && (
                      <button
                        onClick={() => setViewingData({
                          title: `Pending Archive (${archiveData.pending[0].date})`,
                          content: archiveData.pending[0].data || archiveData.pending[0],
                          txId: null
                        })}
                        className="archive-link"
                        style={{ display: 'block', fontSize: '0.7rem', marginTop: '2px', textAlign: 'right', background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', textDecoration: 'underline', width: '100%' }}
                      >
                        <Glyph name="archive" /> INSPECT PENDING
                      </button>
                    )}
                  </span>
                </span>
              ) : (
                <span className="value status-synced">
                  <Glyph name="status" /> ARCHIVED
                  {archiveData.history.length > 0 && (
                    <button
                      onClick={() => setViewingData({
                        title: `Archive Record (${archiveData.history[0].date})`,
                        content: archiveData.history[0], // Full record
                        txId: archiveData.history[0].txId
                      })}
                      className="archive-link"
                      style={{ display: 'block', fontSize: '0.75rem', marginTop: '4px', textAlign: 'right', background: 'none', border: 'none', color: '#90cdf4', cursor: 'pointer', textDecoration: 'underline', width: '100%' }}
                    >
                      <Glyph name="archive" /> VIEW RECORD
                    </button>
                  )}
                </span>
              )}
            </div>

            {/* History Dropdown (Silver/Black) */}
            {archiveData.history.length > 0 && (
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <select
                  onChange={(e) => {
                    const selectedTx = e.target.value;
                    if (!selectedTx) return;
                    const record = archiveData.history.find(h => h.txId === selectedTx);
                    if (record) {
                      setViewingData({
                        title: `Archive Record (${record.date})`,
                        content: record,
                        txId: record.txId
                      });
                    }
                  }}
                  style={{
                    backgroundColor: '#000',
                    color: 'silver',
                    border: '1px solid silver',
                    padding: '4px',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    width: '80%',
                    cursor: 'pointer',
                    outline: 'none',
                    textAlign: 'center'
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>-- SELECT ARCHIVE RECORD --</option>
                  {archiveData.history
                    .filter(h => new Date(h.timestamp).getTime() >= new Date("2025-12-13T03:38:39.319Z").getTime())
                    .map((h, i) => (
                      <option key={h.txId || i} value={h.txId}>
                        {h.date} | TX: {h.txId ? h.txId.slice(0, 8) + '...' : 'LOCAL'}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Schedule Info */}
            <div style={{
              fontSize: '0.75rem',
              color: '#888',
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>
                <strong>Last:</strong>{" "}
                {archiveData.history.length > 0
                  ? new Date(archiveData.history[0].timestamp).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : "None"}
              </span>
              <span>
                <strong>Next:</strong> 23:55 UTC
              </span>
            </div>

            <p style={{
              fontSize: '0.7rem',
              color: '#666',
              fontStyle: 'italic',
              marginTop: '6px',
              textAlign: 'center'
            }}>
              Daily transaction logs are permanently crystalized on Arweave.
              If funds deplete ("Chaos Mode"), data auto-queues until the wallet is refilled.
            </p>
          </div>

          {/* Collective Sentiment Section */}
          {
            sentimentData && (
              <section className="dashboard-card dashboard-card--glow collective-sentiment">
                <h2 className="section-title"><Glyph name="signal" /> Collective Sentiment</h2>
                <p className="sentiment-subtitle">
                  {sentimentData.totalMemos} transmissions analyzed
                </p>

                {/* Calculate percentages */}
                {(() => {
                  const total = Object.values(sentimentData.sentiments).reduce((a, b) => a + b, 0);
                  if (total === 0) return <p>Awaiting first transmission...</p>;

                  const percentages = {
                    hope: Math.round((sentimentData.sentiments.hope / total) * 100),
                    greed: Math.round((sentimentData.sentiments.greed / total) * 100),
                    fear: Math.round((sentimentData.sentiments.fear / total) * 100),
                    mystery: Math.round((sentimentData.sentiments.mystery / total) * 100)
                  };

                  // Determine dominant mood
                  const dominant = Object.keys(sentimentData.sentiments).reduce((a, b) =>
                    sentimentData.sentiments[a] > sentimentData.sentiments[b] ? a : b
                  );

                  const moods = {
                    hope: { text: 'The seed is hopeful' },
                    greed: { text: 'The seed hungers' },
                    fear: { text: 'The seed is uneasy' },
                    mystery: { text: 'The seed is cryptic' }
                  };

                  const currentMood = moods[dominant];

                  return (
                    <>
                      <div className="seed-mood">
                        <Glyph name="pulse" /> {currentMood.text}
                      </div>

                      <div className="sentiment-bars">
                        <div className="sentiment-bar">
                          <span className="sentiment-label">Hope</span>
                          <div className="bar-container">
                            <div className="bar-fill hope" style={{ width: `${percentages.hope}%` }} />
                          </div>
                          <span className="sentiment-value">{percentages.hope}%</span>
                        </div>

                        <div className="sentiment-bar">
                          <span className="sentiment-label">Greed</span>
                          <div className="bar-container">
                            <div className="bar-fill greed" style={{ width: `${percentages.greed}%` }} />
                          </div>
                          <span className="sentiment-value">{percentages.greed}%</span>
                        </div>

                        <div className="sentiment-bar">
                          <span className="sentiment-label">Fear</span>
                          <div className="bar-container">
                            <div className="bar-fill fear" style={{ width: `${percentages.fear}%` }} />
                          </div>
                          <span className="sentiment-value">{percentages.fear}%</span>
                        </div>

                        <div className="sentiment-bar">
                          <span className="sentiment-label">Mystery</span>
                          <div className="bar-container">
                            <div className="bar-fill mystery" style={{ width: `${percentages.mystery}%` }} />
                          </div>
                          <span className="sentiment-value">{percentages.mystery}%</span>
                        </div>
                      </div>

                      {/* Daily Prophecy */}
                      {prophecy && (
                        <div className="daily-prophecy">
                          <h4 className="prophecy-heading"><Glyph name="eye" /> Today's Prophecy</h4>
                          <p className={`prophecy-text ${(() => {
                            // TIME-BASED BLUR GUARD (Production Only)
                            // If it's today's prophecy and before 18:00 UTC, FORCE BLUR
                            // Unless 'forced_ready' is true (manual override by admin)
                            if (isLocalEnvironment()) return ''; // Always visible locally

                            const now = new Date();
                            const todayStr = now.toISOString().split('T')[0];
                            const isToday = prophecy.date === todayStr;
                            const currentHour = now.getUTCHours();
                            const isTooEarly = isToday && currentHour < 18;

                            // If not ready OR (too early AND not forced), then blur
                            if (!prophecy.ready || (isTooEarly && !prophecy.forced_ready)) {
                              return 'prophecy-blur';
                            }
                            return '';
                          })()}`}>
                            {prophecy.text || 'Awaiting the seed\'s vision...'}
                          </p>
                          {!prophecy.ready && !isLocalEnvironment() && (
                            <p className="prophecy-hint">
                              Will be revealed once posted to X.com
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* STATUS FOOTER */}
                <div style={{
                  marginTop: '15px',
                  padding: '8px',
                  borderTop: '1px solid #222',
                  fontSize: '0.75rem',
                  color: '#666',
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  display: 'flex',
                  justifyContent: 'space-between',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '2px'
                }}>
                  <span>Reveals: 18:00 UTC</span>
                  <span>
                    {(() => {
                      if (!sentimentData || !sentimentData.prophecy) return 'Loading...';
                      const p = sentimentData.prophecy;
                      const now = new Date();
                      const todayStr = now.toISOString().split('T')[0];
                      const isToday = p.date === todayStr;
                      const currentHour = now.getUTCHours();
                      const isTooEarly = isToday && currentHour < 18;
                      const shouldBlur = !isLocalEnvironment() && (!p.ready || (isTooEarly && !p.forced_ready));

                      return (
                        <>
                          <span style={{ color: p.ready ? '#0f0' : '#888' }} title="Server Ready Status">RDY:{p.ready ? 'YES' : 'NO'}</span>
                          <span style={{ margin: '0 8px', color: '#444' }}>|</span>
                          <span style={{ color: isTooEarly ? '#ffaa00' : '#888' }} title="Is it before 18:00 UTC?">EARLY:{isTooEarly ? 'YES' : 'NO'}</span>
                          <span style={{ margin: '0 8px', color: '#444' }}>|</span>
                          <span style={{ color: shouldBlur ? '#9966ff' : '#444' }} title="Blur Active?">BLUR:{shouldBlur ? 'ON' : 'OFF'}</span>
                        </>
                      );
                    })()}
                  </span>
                </div>
              </section>
            )}

          {/* Unified Admin System Controls (Local Only) */}
          {isLocalEnvironment() && (
            <div style={{
              marginTop: '10px',
              padding: '15px',
              border: '1px dashed silver', // Silver dashed border
              borderRadius: '4px',
              background: 'rgba(0,0,0,0.3)',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              <strong style={{ color: '#ccc', display: 'block', marginBottom: '15px', fontSize: '0.8rem' }}>ADMIN: SYSTEM CONTROLS (Updated)</strong>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>

                {/* Prophecy Control */}
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <button
                    onClick={async () => {
                      const btn = document.getElementById('prophecy-override-main-btn');
                      const originalText = btn.innerText;
                      btn.innerText = 'Generating...';
                      btn.disabled = true;
                      try {
                        const res = await fetch('/.netlify/functions/manual-trigger-prophecy?reveal=false');
                        const data = await res.json();
                        if (data.success) {
                          alert(`Prophecy generated for ${data.date} (blurred).\nDominated by: ${data.prophecy.dominant.toUpperCase()}`);
                          window.location.reload();
                        } else {
                          alert('Failed: ' + (data.error || data.message));
                        }
                      } catch (e) {
                        alert('Error: ' + e.message);
                      } finally {
                        btn.innerText = originalText;
                        btn.disabled = false;
                      }
                    }}
                    className="blacklist-btn blacklist-btn-add"
                    id="prophecy-override-main-btn"
                    style={{ fontSize: '12px', padding: '8px 16px', backgroundColor: '#6b21a8', border: '1px solid #a855f7', cursor: 'pointer', width: '100%' }}
                  >
                    <Glyph name="eye" /> Force Prophecy (Blurred)
                  </button>
                  <p style={{ fontSize: '9px', marginTop: '6px', color: '#888' }}>
                    Uses <strong>current</strong> blended stats.<br />
                    Overwrites today's prophecy.
                  </p>
                </div>

                {/* Archive Control */}
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <button
                    onClick={async () => {
                      const btn = document.getElementById('archive-force-btn');
                      const originalText = btn.innerText;
                      btn.innerText = 'Archiving...';
                      btn.disabled = true;
                      try {
                        const res = await fetch('/.netlify/functions/manual-trigger-archive');
                        const data = await res.json();
                        if (data.success) {
                          alert(`Archived successfully.\nTX: ${data.txId}`);
                          window.location.reload();
                        } else if (data.chaosMode) {
                          alert(`Upload failed (Chaos Mode Active).\nAdded to pending queue.\nReason: ${data.reason}`);
                          window.location.reload();
                        } else {
                          alert('Failed: ' + (data.error || data.message));
                        }
                      } catch (e) {
                        alert('Error: ' + e.message);
                      } finally {
                        btn.innerText = originalText;
                        btn.disabled = false;
                      }
                    }}
                    className="blacklist-btn blacklist-btn-add"
                    id="archive-force-btn"
                    style={{ fontSize: '12px', padding: '8px 16px', backgroundColor: '#15803d', border: '1px solid #22c55e', cursor: 'pointer', width: '100%' }}
                  >
                    <Glyph name="archive" /> Force Archive (Chaos Check)
                  </button>
                  <p style={{ fontSize: '9px', marginTop: '6px', color: '#888' }}>
                    Uploads current state to Arweave.<br />
                    Adds to Pending Queue if funds fail.
                  </p>
                </div>

              </div>
            </div>
          )}

          {/* Admin-Only Sentiment Rules Visualization */}
          {isLocalEnvironment() && (
            <div style={{
              marginTop: '10px',
              padding: '8px',
              border: '1px dashed #666',
              borderRadius: '4px',
              fontSize: '0.65rem',
              color: '#aaa',
              background: 'rgba(0,0,0,0.3)',
              marginBottom: '1rem'
            }}>
              <strong style={{ color: '#fff', display: 'block', marginBottom: '4px' }}>ADMIN: SENTIMENT LOGIC</strong>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                <div><strong>Sol OUT:</strong> +2 Greed, +1 Fear</div>
                <div><strong>Sol IN:</strong> +2 Hope</div>
                <div><strong>No Memo:</strong> -1 Mystery</div>
                <div><strong>AI Undecided:</strong> +1 Mystery</div>
                <div style={{ gridColumn: 'span 2', marginTop: '2px', fontStyle: 'italic', color: '#888' }}>
                  * Mystery resets to 0% daily on Prophecy Reveal
                </div>
              </div>
            </div>
          )}

          <section className="dashboard-card dashboard-card--glow">
            <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Recent Transactions</span>
              <span className="sows-count">
                {dailyMemoCount} Sows today
              </span>
            </h2>
            <ul id="tx-list" className="tx-list">
              {txItems.length === 0 ? (
                <li className="tx-item">
                  No recent transactions found for this address.
                </li>
              ) : (
                txItems.map((tx, i) => {
                  const timeText = tx.blockTime
                    ? new Date(tx.blockTime * 1000).toLocaleString()
                    : "time unknown";

                  const aiLog =
                    aiLogs && typeof aiLogs[i] === "string"
                      ? aiLogs[i]
                      : "[AI_LOG] awaiting interpretation…";

                  // Direction colors
                  const dirColor = tx.direction === "IN" ? "#a0ffa0" : "#d4a5a5";
                  const dirArrow = tx.direction === "IN" ? "↓ IN" : "↑ OUT";

                  return (
                    <li key={tx.signature || `tx-${i}`} className="tx-item tx-row">
                      {/* Left: existing tx details */}
                      <div className="tx-main">
                        {/* Signature */}
                        <div style={{ marginBottom: "0.25rem" }}>
                          <strong>Signature:</strong>{" "}
                          <a
                            href={`https://solscan.io/tx/${tx.signature}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#c0c0c0", textDecoration: "none" }}
                          >
                            {tx.signature ? `${tx.signature.slice(0, 20)}...${tx.signature.slice(-20)}` : "(no signature)"}
                          </a>
                        </div>

                        {/* Amount & Direction (New) */}
                        {tx.amount && (
                          <div style={{ marginBottom: "0.25rem", color: dirColor, fontWeight: "bold" }}>
                            {dirArrow} {tx.amount} {tx.token}
                          </div>
                        )}

                        {/* Status, Slot, Time on one line */}
                        <div style={{ marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                          <strong>Status:</strong> {tx.confirmationStatus || "unknown"} • {" "}
                          <strong>Slot:</strong>{" "}
                          <a
                            href={`https://solscan.io/block/${tx.slot}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#c0c0c0", textDecoration: "none" }}
                          >
                            {tx.slot ?? "?"}
                          </a> • {" "}
                          <strong>Time:</strong> {timeText}
                        </div>

                        {/* Error if any */}
                        {tx.err && (
                          <div style={{ marginBottom: "0.25rem", color: "#d4a5a5" }}>
                            <strong>Error:</strong> {JSON.stringify(tx.err)}
                          </div>
                        )}

                        {/* Memo if any */}
                        {tx.memo && (
                          <div style={{ marginBottom: "0.25rem" }}>
                            <span className="tx-memo-label">Memo:</span>
                            <span className="tx-memo-text">{tx.memo}</span>
                          </div>
                        )}

                        {/* Account */}
                        <div style={{ fontSize: "0.9rem" }}>
                          <strong>Account:</strong>{" "}
                          <a
                            href={`https://solscan.io/account/${BAD_SEED_WALLET_ADDRESS}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#c0c0c0", textDecoration: "none" }}
                          >
                            {BAD_SEED_WALLET_ADDRESS.slice(0, 20)}...
                          </a>
                        </div>
                      </div>


                      {/* Right: AI terminal toast */}
                      <div className="tx-ai-log">
                        <div className="tx-ai-header">[BADSEED AI LOG]</div>
                        <div className="tx-ai-body">{aiLog}</div>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </section >

          {/* X.com Post Queue section */}
          < section id="queue-section" className="dashboard-card dashboard-card--glow post-queue-section" >
            <h2 className="section-title"><Glyph name="signal" /> X.com Post Queue</h2>

            <div className="queue-status">
              <div className="queue-stat">
                <span className="queue-stat-label">Posts Today:</span>
                <span className="queue-stat-value">{dailyPostCount} / 2</span>
              </div>
              <div className="queue-stat">
                <span className="queue-stat-label">Queued:</span>
                <span className="queue-stat-value">{postQueue.length}</span>
              </div>
              <div className="queue-stat">
                <span className="queue-stat-label">Next Post:</span>
                <span className="queue-stat-value">
                  {nextPostTime ? new Date(nextPostTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }) : 'N/A'}
                </span>
              </div>
            </div>

            {/* Manual Queue Controls - Dev or Admin Only */}
            {
              (process.env.NODE_ENV === 'development' || (publicKey && publicKey.toBase58() === BAD_SEED_WALLET_ADDRESS)) && (
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', justifyContent: 'center' }}>
                  <button
                    onClick={async () => {
                      const result = await forcePostNow();
                      if (result.success) {
                        alert("Post sent successfully!");
                      } else {
                        alert("Post failed: " + result.reason);
                      }
                      updateQueueDisplay();
                    }}
                    className="blacklist-btn"
                    style={{ fontSize: '0.8rem', padding: '5px 10px' }}
                  >
                    <Glyph name="pulse" /> Force Post Now
                  </button>
                  <button
                    onClick={() => {
                      addTestItem();
                      updateQueueDisplay();
                    }}
                    className="blacklist-btn"
                    style={{ fontSize: '0.8rem', padding: '5px 10px' }}
                  >
                    Add Test Item
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm("Are you sure you want to clear the queue? This will remove all pending posts.")) {
                        await clearQueue();
                        updateQueueDisplay();
                      }
                    }}
                    className="blacklist-btn"
                    style={{ fontSize: '0.8rem', padding: '5px 10px', backgroundColor: '#ff4444' }}
                  >
                    Clear Queue
                  </button>
                </div>
              )
            }

            <div className="queue-items">
              {postQueue.length === 0 ? (
                <div className="queue-empty">
                  <p>No posts queued. Memos will automatically queue when new transactions with memos are detected.</p>
                </div>
              ) : (
                <ul className="queue-list">
                  {postQueue.map((item, idx) => (
                    <li key={idx} className="queue-item">
                      <div className="queue-item-header">
                        <span className="queue-item-number">#{idx + 1}</span>
                        <span className="queue-item-time">
                          {item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                      <div className="queue-item-content">
                        <div className="queue-memo">
                          <span className="queue-label">Memo:</span>
                          <span className="queue-text">"{item.memo}"</span>
                        </div>
                        <div className="queue-ai">
                          <span className="queue-label">→ AI:</span>
                          <span className="queue-text">{item.aiLog}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="queue-info">
              <p>Posts are sent twice daily (UTC midnight & noon). Limit: 2 posts/day.</p>
              <p>Duplicate memos are automatically filtered.</p>
            </div>
          </section>

          {/* Blacklist section */}
          <section className="dashboard-card dashboard-card--glow blacklist-section">
            <h2 className="section-title"><Glyph name="warning" /> Blacklisted Addresses</h2>

            <div className="blacklist-stats">
              <div className="blacklist-stat">
                <span className="blacklist-stat-label">Blacklisted Addresses:</span>
                <span className="blacklist-stat-value">{blacklist.length}</span>
              </div>
              <div className="blacklist-stat">
                <span className="blacklist-stat-label">Filtered Transactions:</span>
                <span className="blacklist-stat-value">{blacklistedTxs.length}</span>
              </div>
            </div>

            {/* Add address input - only show in local development */}
            {isLocalEnvironment() && (
              <div className="blacklist-add">
                <input
                  type="text"
                  className="blacklist-input"
                  placeholder="Enter Solana address to blacklist..."
                  value={newBlacklistAddress}
                  onChange={(e) => setNewBlacklistAddress(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddToBlacklist();
                    }
                  }}
                />
                <button
                  className="blacklist-btn blacklist-btn-add"
                  onClick={handleAddToBlacklist}
                >
                  Add Address
                </button>
                <button
                  className="blacklist-btn blacklist-btn-copy"
                  onClick={handleCopyBlacklist}
                  title="Copy blacklist array to clipboard for code update"
                >
                  <Glyph name="copy" /> Copy Blacklist for Code
                </button>
              </div>
            )}

            <div className="blacklist-list">
              {blacklist.length === 0 ? (
                <div className="blacklist-empty">
                  <p>No addresses blacklisted. Add addresses above to filter unwanted transactions.</p>
                </div>
              ) : (
                <ul className="blacklist-addresses">
                  {blacklist.map((address, idx) => (
                    <li key={idx} className="blacklist-item">
                      <span className="blacklist-address">{address}</span>
                      {/* Remove button - only show in local development */}
                      {isLocalEnvironment() && (
                        <button
                          className="blacklist-btn blacklist-btn-remove"
                          onClick={() => handleRemoveFromBlacklist(address)}
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {blacklistedTxs.length > 0 && (
              <details className="blacklist-transactions">
                <summary className="blacklist-tx-summary">
                  View {blacklistedTxs.length} Filtered Transaction{blacklistedTxs.length !== 1 ? 's' : ''}
                </summary>
                <ul className="blacklist-tx-list">
                  {blacklistedTxs.map((tx, idx) => (
                    <li key={idx} className="blacklist-tx-item">
                      <div className="blacklist-tx-info">
                        <span className="blacklist-tx-sig">
                          <a
                            href={`https://solscan.io/tx/${tx.signature}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {tx.signature.slice(0, 20)}...
                          </a>
                        </span>
                        <span className="blacklist-tx-time">
                          {tx.blockTime ? new Date(tx.blockTime * 1000).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        </div>
      </main >
      {/* Data Viewer Modal (Archive & Pending) */}
      {
        viewingData && (
          <div className="modal-overlay" onClick={() => setViewingData(null)}>
            <div className="send-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
              <h2><Glyph name="archive" /> {viewingData.title || "Archive Record"}</h2>

              <div style={{ maxHeight: '60vh', overflowY: 'auto', background: '#111', padding: '10px', borderRadius: '4px', border: '1px solid #333' }}>
                {viewingData.txId && (
                  <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #333' }}>
                    <strong>Arweave TX:</strong> <a href={`https://gateway.irys.xyz/${viewingData.txId}`} target="_blank" rel="noreferrer" style={{ color: '#90cdf4' }}>View on Permaweb</a>
                    <br /><span style={{ fontSize: '0.8rem', color: '#666' }}>Note: Gateway resolution is usually instant, but may vary.</span>
                  </div>
                )}
                <pre style={{ color: '#0f0', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                  {JSON.stringify(viewingData.content, null, 2)}
                </pre>
              </div>

              <div className="modal-buttons" style={{ marginTop: '15px' }}>
                <button
                  className="modal-btn modal-btn-cancel"
                  onClick={() => setViewingData(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Send Message Modal */}
      {
        showSendModal && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target.className === 'modal-overlay') {
              // Don't close
            }
          }}>
            <div className="send-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Send a Transmission to the Seed</h2>

              <div style={{ marginBottom: "1rem" }}>
                <label>Message (Memo)</label>
                <textarea
                  value={memoText}
                  onChange={(e) => setMemoText(e.target.value)}
                  placeholder="Type your message here..."
                  maxLength={100}
                />
                <div style={{ textAlign: "right", fontSize: "0.8rem", color: "#666" }}>
                  {memoText.length}/100
                </div>
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label>Amount (SOL)</label>
                <input
                  type="number"
                  step="0.001"
                  min="0.001"
                  value={solAmount}
                  onChange={(e) => setSolAmount(e.target.value)}
                />
                <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "0.2rem" }}>
                  Minimum: 0.001 SOL
                </div>
              </div>

              {sendError && (
                <div className="modal-error">
                  {sendError}
                </div>
              )}

              {sendSuccess && (
                <div className="modal-success">
                  Transmission sent successfully!
                </div>
              )}

              <div className="modal-buttons">
                <button
                  className="modal-btn modal-btn-cancel"
                  onClick={() => setShowSendModal(false)}
                  disabled={isSending}
                >
                  Cancel
                </button>
                <button
                  className="modal-btn modal-btn-send"
                  onClick={handleSendTransaction}
                  disabled={isSending}
                >
                  {isSending ? "Sending..." : "Send Transmission"}
                </button>
              </div>

              <div className="modal-info">
                This will send SOL to the BAD SEED wallet with your message attached on-chain.
              </div>
            </div>
          </div>
        )
      }

      {/* Floating Scroll Buttons */}
      {
        showDashboard && (
          <div className="scroll-buttons">
            <button
              className="scroll-btn"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              title="Scroll to Top"
            >
              ▲ Top
            </button>
            <button
              className="scroll-btn"
              onClick={() => {
                const queueSection = document.getElementById('queue-section');
                if (queueSection) queueSection.scrollIntoView({ behavior: 'smooth' });
              }}
              title="Scroll to Queue"
            >
              ▼ Queue
            </button>
          </div>
        )
      }

    </div >
  );
}

export default App;
